// ============================================================
// TOUCH CONTROLS
// 3x3 arrow grid and A button for mobile
// ============================================================

export interface TouchInputState {
    left: boolean;
    right: boolean;
    up: boolean;
    down: boolean;
    downJustPressed: boolean;
    attack: boolean;
    attackJustPressed: boolean;
    talkJustPressed: boolean;
    anyJustPressed: boolean;
}

// Grid position: 0-8, -1 = none
// Layout:
// 0 1 2   (up-left, up, up-right)
// 3 4 5   (left, center, right)
// 6 7 8   (down-left, down, down-right)
type GridPosition = -1 | 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8;

// Map grid position to directional inputs
const GRID_TO_INPUT: Record<GridPosition, { left: boolean; right: boolean; up: boolean; down: boolean }> = {
    [-1]: { left: false, right: false, up: false, down: false },
    [0]: { left: true, right: false, up: true, down: false },   // up-left
    [1]: { left: false, right: false, up: true, down: false },  // up
    [2]: { left: false, right: true, up: true, down: false },   // up-right
    [3]: { left: true, right: false, up: false, down: false },  // left
    [4]: { left: false, right: false, up: false, down: false }, // center (nothing)
    [5]: { left: false, right: true, up: false, down: false },  // right
    [6]: { left: true, right: false, up: false, down: true },   // down-left
    [7]: { left: false, right: false, up: false, down: true },  // down
    [8]: { left: false, right: true, up: false, down: true },   // down-right
};

export class TouchControls {
    private enabled: boolean = false;

    // Input state
    public state: TouchInputState = {
        left: false,
        right: false,
        up: false,
        down: false,
        downJustPressed: false,
        attack: false,
        attackJustPressed: false,
        talkJustPressed: false,
        anyJustPressed: false,
    };

    // DOM elements
    private container!: HTMLDivElement;
    private arrowGrid!: HTMLDivElement;
    private aButton!: HTMLDivElement;
    private tButton!: HTMLDivElement;
    private gridCells: HTMLDivElement[] = [];

    // Touch tracking
    private arrowPointerId: number | null = null;
    private aButtonPointerId: number | null = null;
    private currentGridPos: GridPosition = -1;
    private lastDownState: boolean = false;
    private lastAttackState: boolean = false;

    // Layout constants
    private readonly MARGIN = 10;
    private readonly GAP = 10; // Gap between arrow grid and A button

    constructor(_scene: Phaser.Scene) {
        // Detect if mobile
        this.detectMobile();
    }

    private detectMobile(): void {
        // Check for touch capability and mobile user agent
        const hasTouchScreen = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
        const isMobileUA = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
        this.enabled = hasTouchScreen && isMobileUA;
    }

    create(): void {
        if (!this.enabled) return;

        // Create DOM container for touch controls
        this.container = document.createElement('div');
        this.container.id = 'touch-controls';
        this.container.style.cssText = `
            position: fixed;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            pointer-events: none;
            z-index: 1000;
            touch-action: none;
        `;
        document.body.appendChild(this.container);

        // Create 3x3 arrow grid
        this.createArrowGrid();

        // Create A button
        this.createAButton();

        // Create T (talk) button
        this.createTButton();

        // Update layout based on orientation
        this.updateLayout();

        // Listen for orientation changes
        window.addEventListener('resize', this.onResize.bind(this));
        window.addEventListener('orientationchange', this.onResize.bind(this));
    }

    private createArrowGrid(): void {
        this.arrowGrid = document.createElement('div');
        this.arrowGrid.style.cssText = `
            position: absolute;
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            grid-template-rows: repeat(3, 1fr);
            pointer-events: auto;
            touch-action: none;
            user-select: none;
            -webkit-user-select: none;
        `;

        // Arrow symbols for each cell
        const arrows = ['↖', '↑', '↗', '←', '•', '→', '↙', '↓', '↘'];

        for (let i = 0; i < 9; i++) {
            const cell = document.createElement('div');
            cell.dataset.index = String(i);
            cell.style.cssText = `
                display: flex;
                align-items: center;
                justify-content: center;
                background: rgba(0, 0, 0, 0.4);
                border: 1px solid rgba(255, 255, 255, 0.2);
                color: rgba(255, 255, 255, 0.6);
                font-size: 28px;
                touch-action: none;
                user-select: none;
                -webkit-user-select: none;
            `;
            cell.textContent = arrows[i];
            this.gridCells.push(cell);
            this.arrowGrid.appendChild(cell);
        }

        // Touch events on grid
        this.arrowGrid.addEventListener('touchstart', this.onArrowTouchStart.bind(this), { passive: false });
        this.arrowGrid.addEventListener('touchmove', this.onArrowTouchMove.bind(this), { passive: false });
        this.arrowGrid.addEventListener('touchend', this.onArrowTouchEnd.bind(this), { passive: false });
        this.arrowGrid.addEventListener('touchcancel', this.onArrowTouchEnd.bind(this), { passive: false });

        this.container.appendChild(this.arrowGrid);
    }

    private createAButton(): void {
        this.aButton = document.createElement('div');
        this.aButton.style.cssText = `
            position: absolute;
            border-radius: 50%;
            background: rgba(220, 50, 50, 0.5);
            border: 4px solid rgba(255, 100, 100, 0.6);
            display: flex;
            align-items: center;
            justify-content: center;
            color: white;
            font-size: 36px;
            font-weight: bold;
            font-family: sans-serif;
            pointer-events: auto;
            touch-action: none;
            user-select: none;
            -webkit-user-select: none;
        `;
        this.aButton.textContent = 'A';

        // Touch events on A button
        this.aButton.addEventListener('touchstart', this.onAButtonTouchStart.bind(this), { passive: false });
        this.aButton.addEventListener('touchend', this.onAButtonTouchEnd.bind(this), { passive: false });
        this.aButton.addEventListener('touchcancel', this.onAButtonTouchEnd.bind(this), { passive: false });

        this.container.appendChild(this.aButton);
    }

    private createTButton(): void {
        this.tButton = document.createElement('div');
        this.tButton.style.cssText = `
            position: absolute;
            border-radius: 50%;
            background: rgba(50, 130, 220, 0.5);
            border: 3px solid rgba(100, 170, 255, 0.6);
            display: flex;
            align-items: center;
            justify-content: center;
            color: white;
            font-size: 24px;
            font-weight: bold;
            font-family: sans-serif;
            pointer-events: auto;
            touch-action: none;
            user-select: none;
            -webkit-user-select: none;
        `;
        this.tButton.textContent = 'T';

        this.tButton.addEventListener('touchstart', this.onTButtonTouchStart.bind(this), { passive: false });

        this.container.appendChild(this.tButton);
    }

    private onTButtonTouchStart(e: TouchEvent): void {
        e.preventDefault();
        this.state.talkJustPressed = true;
        this.state.anyJustPressed = true;

        // Visual feedback
        this.tButton.style.background = 'rgba(100, 170, 255, 0.7)';
        setTimeout(() => {
            this.tButton.style.background = 'rgba(50, 130, 220, 0.5)';
        }, 150);
    }

    private onArrowTouchStart(e: TouchEvent): void {
        e.preventDefault();
        this.state.anyJustPressed = true;

        // Only track first touch on arrow grid
        if (this.arrowPointerId !== null) return;

        const touch = e.changedTouches[0];
        this.arrowPointerId = touch.identifier;
        this.updateGridFromTouch(touch);
    }

    private onArrowTouchMove(e: TouchEvent): void {
        e.preventDefault();

        // Find our tracked touch
        for (let i = 0; i < e.changedTouches.length; i++) {
            const touch = e.changedTouches[i];
            if (touch.identifier === this.arrowPointerId) {
                this.updateGridFromTouch(touch);
                break;
            }
        }
    }

    private onArrowTouchEnd(e: TouchEvent): void {
        e.preventDefault();

        // Check if our tracked touch ended
        for (let i = 0; i < e.changedTouches.length; i++) {
            const touch = e.changedTouches[i];
            if (touch.identifier === this.arrowPointerId) {
                // Release - clear all directional input
                this.arrowPointerId = null;
                this.setGridPosition(-1);
                break;
            }
        }
    }

    private updateGridFromTouch(touch: Touch): void {
        const rect = this.arrowGrid.getBoundingClientRect();
        const x = touch.clientX - rect.left;
        const y = touch.clientY - rect.top;

        // Check if still within grid bounds
        if (x < 0 || x >= rect.width || y < 0 || y >= rect.height) {
            // Outside grid - but DON'T release until touch ends!
            // Keep current state
            return;
        }

        // Calculate grid cell
        const cellWidth = rect.width / 3;
        const cellHeight = rect.height / 3;
        const col = Math.floor(x / cellWidth);
        const row = Math.floor(y / cellHeight);
        const index = row * 3 + col;

        this.setGridPosition(index as GridPosition);
    }

    private setGridPosition(pos: GridPosition): void {
        // Update visual state
        if (this.currentGridPos !== -1 && this.currentGridPos !== pos) {
            this.gridCells[this.currentGridPos].style.background = 'rgba(0, 0, 0, 0.4)';
        }
        if (pos !== -1) {
            this.gridCells[pos].style.background = 'rgba(255, 255, 255, 0.3)';
        }

        this.currentGridPos = pos;

        // Update input state from grid position
        const inputs = GRID_TO_INPUT[pos];

        // Track downJustPressed
        const wasDown = this.state.down;

        this.state.left = inputs.left;
        this.state.right = inputs.right;
        this.state.up = inputs.up;
        this.state.down = inputs.down;

        // downJustPressed: true if down just became true
        if (this.state.down && !wasDown) {
            this.state.downJustPressed = true;
        }
    }

    private onAButtonTouchStart(e: TouchEvent): void {
        e.preventDefault();
        this.state.anyJustPressed = true;

        // Only track first touch on A button
        if (this.aButtonPointerId !== null) return;

        const touch = e.changedTouches[0];
        this.aButtonPointerId = touch.identifier;

        // Set attack state
        this.state.attack = true;
        this.state.attackJustPressed = !this.lastAttackState;
        this.lastAttackState = true;

        // Visual feedback
        this.aButton.style.background = 'rgba(255, 100, 100, 0.7)';
    }

    private onAButtonTouchEnd(e: TouchEvent): void {
        e.preventDefault();

        // Check if our tracked touch ended
        for (let i = 0; i < e.changedTouches.length; i++) {
            const touch = e.changedTouches[i];
            if (touch.identifier === this.aButtonPointerId) {
                this.aButtonPointerId = null;
                this.state.attack = false;
                this.lastAttackState = false;

                // Visual feedback
                this.aButton.style.background = 'rgba(220, 50, 50, 0.5)';
                break;
            }
        }
    }

    private updateLayout(): void {
        const screenWidth = window.innerWidth;
        const screenHeight = window.innerHeight;
        const isLandscape = screenWidth > screenHeight;

        // Find the game container to know where controls should go
        const gameContainer = document.getElementById('game-container');
        let availableHeight = screenHeight;
        let controlsTop = 0;

        if (gameContainer) {
            const gameRect = gameContainer.getBoundingClientRect();
            // Available space below the game
            availableHeight = screenHeight - gameRect.bottom;
            controlsTop = gameRect.bottom;
        }

        // Calculate sizes based on available space
        // Each control takes roughly half the width
        const halfWidth = (screenWidth - this.MARGIN * 2 - this.GAP) / 2;

        // Grid should be square, limited by available height or half width
        const maxGridSize = Math.min(halfWidth, availableHeight - this.MARGIN * 2);
        const gridSize = Math.max(100, maxGridSize); // Minimum 100px

        // A button is circular, same size constraints
        const aButtonSize = Math.max(80, maxGridSize * 0.7); // Slightly smaller than grid

        // Position arrow grid on left
        this.arrowGrid.style.width = `${gridSize}px`;
        this.arrowGrid.style.height = `${gridSize}px`;
        this.arrowGrid.style.left = `${this.MARGIN}px`;
        this.arrowGrid.style.top = `${controlsTop + (availableHeight - gridSize) / 2}px`;
        this.arrowGrid.style.bottom = 'auto';
        this.arrowGrid.style.right = 'auto';

        // T button is smaller than A
        const tButtonSize = Math.max(50, aButtonSize * 0.55);

        // Position A button on right, vertically centered in available space
        // Shift down slightly to make room for T button above
        const aButtonRight = this.MARGIN + (halfWidth - aButtonSize) / 2;
        const aButtonTop = controlsTop + (availableHeight - aButtonSize) / 2 + tButtonSize * 0.3;
        this.aButton.style.width = `${aButtonSize}px`;
        this.aButton.style.height = `${aButtonSize}px`;
        this.aButton.style.right = `${aButtonRight}px`;
        this.aButton.style.top = `${aButtonTop}px`;
        this.aButton.style.left = 'auto';
        this.aButton.style.bottom = 'auto';

        // Position T button above A button, right-aligned with A's center
        const aButtonCenterRight = aButtonRight + aButtonSize / 2;
        this.tButton.style.width = `${tButtonSize}px`;
        this.tButton.style.height = `${tButtonSize}px`;
        this.tButton.style.right = `${aButtonCenterRight - tButtonSize / 2}px`;
        this.tButton.style.top = `${aButtonTop - tButtonSize - this.GAP}px`;
        this.tButton.style.left = 'auto';
        this.tButton.style.bottom = 'auto';

        // Update font sizes based on control sizes
        const arrowFontSize = Math.max(20, gridSize / 5);
        this.gridCells.forEach(cell => {
            cell.style.fontSize = `${arrowFontSize}px`;
        });

        const aFontSize = Math.max(24, aButtonSize / 2.5);
        this.aButton.style.fontSize = `${aFontSize}px`;

        const tFontSize = Math.max(16, tButtonSize / 2.5);
        this.tButton.style.fontSize = `${tFontSize}px`;
    }

    private onResize(): void {
        this.updateLayout();
    }

    // Call this each frame to clear just-pressed states
    clearJustPressed(): void {
        this.state.downJustPressed = false;
        this.state.attackJustPressed = false;
        this.state.talkJustPressed = false;
        this.state.anyJustPressed = false;
    }

    isEnabled(): boolean {
        return this.enabled;
    }

    destroy(): void {
        if (this.container && this.container.parentNode) {
            this.container.parentNode.removeChild(this.container);
        }
        window.removeEventListener('resize', this.onResize.bind(this));
        window.removeEventListener('orientationchange', this.onResize.bind(this));
    }
}
